import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, Plus, Send, Bug, Sparkles, Wrench, ChevronRight, Clock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  issue_resolution: { label: 'Issue Resolution', icon: Bug, color: 'bg-red-100 text-red-700 border-red-200' },
  new_feature: { label: 'New Feature', icon: Sparkles, color: 'bg-violet-100 text-violet-700 border-violet-200' },
  feature_enhancement: { label: 'Enhancement', icon: Wrench, color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  resolved: { label: 'Resolved', color: 'bg-emerald-100 text-emerald-700' },
  closed: { label: 'Closed', color: 'bg-slate-200 text-slate-600' },
};

export default function TenantSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ category: 'issue_resolution', subject: '', message: '' });
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/support/tickets`, { headers: getHeaders() });
      setTickets(res.data);
      if (selectedTicket) {
        const updated = res.data.find(t => t.id === selectedTicket.id);
        if (updated) setSelectedTicket(updated);
      }
    } catch (e) { console.error('Failed to load tickets:', e); toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  }, [selectedTicket]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedTicket?.messages]);

  const handleCreate = async () => {
    if (!createForm.subject.trim() || !createForm.message.trim()) {
      toast.error('Subject and message are required'); return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/support/tickets`, createForm, { headers: getHeaders() });
      toast.success('Support ticket created');
      setShowCreate(false);
      setCreateForm({ category: 'issue_resolution', subject: '', message: '' });
      fetchTickets();
      setSelectedTicket(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create ticket'); }
    finally { setSending(false); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/tickets/${selectedTicket.id}/messages`, { message: replyText }, { headers: getHeaders() });
      setReplyText('');
      fetchTickets();
    } catch { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;

  return (
    <div data-testid="tenant-support-page" className="flex gap-5 h-[calc(100vh-140px)]">
      {/* Ticket List */}
      <div className="w-96 shrink-0 bg-white border border-blue-100 rounded-xl shadow-sm flex flex-col overflow-hidden">
        <div className="p-4 border-b border-blue-100">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-extrabold text-blue-900 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <MessageSquare size={18} className="inline mr-2" />Support
            </h2>
            <Button size="sm" onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs" data-testid="new-ticket-btn">
              <Plus size={14} className="mr-1" />New Ticket
            </Button>
          </div>
          <p className="text-[10px] text-slate-400">Chat with Platform Admin</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {tickets.length === 0 ? (
            <div className="text-center py-12 px-4">
              <MessageSquare size={32} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-400">No support tickets yet</p>
              <Button size="sm" variant="outline" className="mt-3 text-xs" onClick={() => setShowCreate(true)}>Create your first ticket</Button>
            </div>
          ) : tickets.map(ticket => {
            const cat = CATEGORY_CONFIG[ticket.category] || CATEGORY_CONFIG.issue_resolution;
            const status = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
            const lastMsg = ticket.messages?.[ticket.messages.length - 1];
            return (
              <button key={ticket.id} onClick={() => setSelectedTicket(ticket)}
                className={`w-full text-left p-3.5 border-b border-slate-100 hover:bg-blue-50/50 transition-colors ${selectedTicket?.id === ticket.id ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}`}
                data-testid={`ticket-${ticket.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={`text-[9px] font-bold border ${cat.color}`}>{cat.label}</Badge>
                  <Badge className={`text-[9px] font-bold ${status.color}`}>{status.label}</Badge>
                </div>
                <p className="text-sm font-semibold text-slate-800 truncate">{ticket.subject}</p>
                <p className="text-[10px] text-slate-400 truncate mt-0.5">
                  {lastMsg?.sender === 'admin' ? 'Admin: ' : ''}{lastMsg?.content?.slice(0, 60)}...
                </p>
                <p className="text-[9px] text-slate-300 mt-1">{ticket.updated_at ? new Date(ticket.updated_at).toLocaleDateString() : ''}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 bg-white border border-blue-100 rounded-xl shadow-sm flex flex-col overflow-hidden">
        {selectedTicket ? (
          <>
            {/* Header */}
            <div className="p-4 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-white">
              <div className="flex items-center gap-2">
                <Badge className={`text-[9px] font-bold border ${CATEGORY_CONFIG[selectedTicket.category]?.color}`}>
                  {CATEGORY_CONFIG[selectedTicket.category]?.label}
                </Badge>
                <Badge className={`text-[9px] font-bold ${STATUS_CONFIG[selectedTicket.status]?.color}`}>
                  {STATUS_CONFIG[selectedTicket.status]?.label}
                </Badge>
              </div>
              <h3 className="font-bold text-blue-900 mt-1">{selectedTicket.subject}</h3>
              <p className="text-[10px] text-slate-400">Created by {selectedTicket.created_by} on {new Date(selectedTicket.created_at).toLocaleDateString()}</p>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50">
              {(selectedTicket.messages || []).map(msg => (
                <div key={msg.id} className={`flex ${msg.sender === 'tenant' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] rounded-xl px-4 py-2.5 ${
                    msg.sender === 'tenant'
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'
                  }`}>
                    <p className={`text-[10px] font-bold mb-0.5 ${msg.sender === 'tenant' ? 'text-blue-200' : 'text-violet-600'}`}>
                      {msg.sender === 'admin' ? 'Platform Admin' : msg.sender_name}
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <p className={`text-[9px] mt-1 ${msg.sender === 'tenant' ? 'text-blue-300' : 'text-slate-400'}`}>
                      {new Date(msg.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply */}
            {selectedTicket.status !== 'closed' && (
              <div className="p-3 border-t border-blue-100 flex gap-2">
                <Input value={replyText} onChange={e => setReplyText(e.target.value)}
                  placeholder="Type your message..." className="flex-1 border-blue-200 text-sm"
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); } }}
                  data-testid="support-reply-input" />
                <Button onClick={handleReply} disabled={sending || !replyText.trim()} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="support-send-btn">
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-center">
            <div>
              <MessageSquare size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-semibold">Select a ticket or create a new one</p>
              <p className="text-xs text-slate-400 mt-1">Communicate directly with the Platform Admin</p>
            </div>
          </div>
        )}
      </div>

      {/* Create Ticket Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-bold text-blue-900 text-lg">New Support Ticket</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-bold uppercase text-blue-900">Category</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                  <button key={key} onClick={() => setCreateForm({ ...createForm, category: key })}
                    className={`p-3 rounded-lg border text-center transition-colors ${createForm.category === key ? 'bg-blue-50 border-blue-400 ring-1 ring-blue-400' : 'border-slate-200 hover:border-blue-300'}`}
                    data-testid={`cat-${key}`}>
                    <cfg.icon size={20} className="mx-auto mb-1 text-blue-600" />
                    <p className="text-[10px] font-bold text-slate-700">{cfg.label}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-blue-900">Subject</Label>
              <Input value={createForm.subject} onChange={e => setCreateForm({ ...createForm, subject: e.target.value })}
                placeholder="Brief summary of your request" className="mt-1 border-blue-200" data-testid="ticket-subject" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-blue-900">Message</Label>
              <Textarea value={createForm.message} onChange={e => setCreateForm({ ...createForm, message: e.target.value })}
                placeholder="Describe the issue, feature, or improvement in detail..."
                className="mt-1 border-blue-200 min-h-[120px]" data-testid="ticket-message" />
            </div>
            <Button onClick={handleCreate} disabled={sending} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold" data-testid="submit-ticket-btn">
              {sending ? <><Loader2 size={16} className="mr-2 animate-spin" />Sending...</> : <><Send size={16} className="mr-2" />Submit Ticket</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
